/**
 * CVA-Motion Integration Utilities
 *
 * This module provides utilities to seamlessly integrate Class Variance Authority (CVA)
 * with Motion (Framer Motion) for creating first-class UI elements with modern animations.
 *
 * Features:
 * - Type-safe motion variants based on CVA variants
 * - Automatic animation presets for common interactions
 * - Hover, tap, and focus animations
 * - Reduced motion support
 * - Performance-optimized animations
 *
 * @example
 * ```tsx
 * import { createMotionComponent } from '@/lib/cva-motion';
 * import { buttonVariants } from '@/components/ui/button';
 *
 * const MotionButton = createMotionComponent(buttonVariants, {
 *   hover: 'scale',
 *   tap: 'scale',
 *   initial: 'subtle',
 * });
 *
 * <MotionButton variant="emerald" size="lg">
 *   Click me
 * </MotionButton>
 * ```
 */

import * as React from 'react';

import type { VariantProps } from 'class-variance-authority';
import { motion, useReducedMotion, type HTMLMotionProps, type Variants } from 'motion/react';

import { ANIMATION_DURATIONS, EASING, hoverScale, hoverLift } from './animations';

/**
 * Animation intensity levels for different use cases.
 */
export type AnimationIntensity = 'none' | 'subtle' | 'moderate' | 'pronounced';

/**
 * Motion interaction types for CVA components.
 */
export type MotionInteraction = 'scale' | 'lift' | 'glow' | 'none';

/**
 * Configuration for motion-enhanced CVA components.
 */
export interface MotionCVAConfig {
  /**
   * Hover animation type
   * @default 'scale'
   */
  hover?: MotionInteraction;
  /**
   * Tap/press animation type
   * @default 'scale'
   */
  tap?: MotionInteraction;
  /**
   * Focus animation type
   * @default 'glow'
   */
  focus?: MotionInteraction;
  /**
   * Initial animation intensity
   * @default 'subtle'
   */
  initial?: AnimationIntensity;
  /**
   * Whether to animate on mount
   * @default true
   */
  animateOnMount?: boolean;
  /**
   * Custom motion variants
   */
  variants?: Variants;
  /**
   * Custom transition overrides
   */
  transition?: {
    duration?: number;
    ease?: number[] | string;
    type?: 'spring' | 'tween';
    stiffness?: number;
    damping?: number;
  };
}

/**
 * Default motion configuration for CVA components.
 */
const DEFAULT_MOTION_CONFIG: Required<Omit<MotionCVAConfig, 'variants' | 'transition'>> = {
  hover: 'scale',
  tap: 'scale',
  focus: 'glow',
  initial: 'subtle',
  animateOnMount: true,
};

/**
 * Creates hover animation based on interaction type.
 */
function getHoverAnimation(type: MotionInteraction) {
  switch (type) {
    case 'scale':
      return hoverScale;
    case 'lift':
      return hoverLift;
    case 'glow':
      return {
        filter: 'brightness(1.1)',
        transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeOut },
      };
    case 'none':
      return {};
    default:
      return hoverScale;
  }
}

/**
 * Creates tap/press animation based on interaction type.
 */
function getTapAnimation(type: MotionInteraction) {
  switch (type) {
    case 'scale':
      return { scale: 0.98 };
    case 'lift':
      return { y: 0 };
    case 'glow':
      return { filter: 'brightness(0.95)' };
    case 'none':
      return {};
    default:
      return { scale: 0.98 };
  }
}

/**
 * Creates focus animation based on interaction type.
 */
function getFocusAnimation(type: MotionInteraction) {
  switch (type) {
    case 'glow':
      return {
        scale: 1.01,
        filter: 'brightness(1.05)',
        transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeOut },
      };
    case 'scale':
      return { scale: 1.02 };
    case 'lift':
      return { y: -1 };
    case 'none':
      return {};
    default:
      return {
        scale: 1.01,
        filter: 'brightness(1.05)',
        transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeOut },
      };
  }
}

/**
 * Creates initial animation variants based on intensity.
 */
function getInitialVariants(intensity: AnimationIntensity): Variants {
  if (intensity === 'none') {
    return {
      hidden: { opacity: 1 },
      visible: { opacity: 1 },
    };
  }

  const variants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: ANIMATION_DURATIONS.normal,
        ease: EASING.easeOut,
      },
    },
  };

  switch (intensity) {
    case 'subtle':
      variants.hidden = { opacity: 0 };
      variants.visible = {
        opacity: 1,
        transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeOut },
      };
      break;
    case 'moderate':
      variants.hidden = { opacity: 0, y: 5 };
      variants.visible = {
        opacity: 1,
        y: 0,
        transition: {
          duration: ANIMATION_DURATIONS.normal,
          ease: EASING.easeOut,
          type: 'spring',
          stiffness: 300,
          damping: 30,
        },
      };
      break;
    case 'pronounced':
      variants.hidden = { opacity: 0, y: 10, scale: 0.95 };
      variants.visible = {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration: ANIMATION_DURATIONS.normal,
          ease: EASING.easeOut,
          type: 'spring',
          stiffness: 400,
          damping: 25,
        },
      };
      break;
  }

  return variants;
}

/**
 * Creates a motion-enhanced component from a CVA variant function.
 *
 * This utility bridges CVA variants with Motion animations, providing:
 * - Type-safe variant props
 * - Automatic hover/tap/focus animations
 * - Initial mount animations
 * - Reduced motion support
 * - Performance optimizations
 *
 * @param cvaVariants - The CVA variant function (e.g., buttonVariants)
 * @param config - Motion configuration options
 * @returns A motion-enhanced component with CVA variant support
 *
 * @example
 * ```tsx
 * import { createMotionComponent } from '@/lib/cva-motion';
 * import { buttonVariants } from '@/components/ui/button';
 *
 * const MotionButton = createMotionComponent(buttonVariants, {
 *   hover: 'scale',
 *   tap: 'scale',
 *   initial: 'subtle',
 * });
 *
 * <MotionButton variant="emerald" size="lg">
 *   Click me
 * </MotionButton>
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CvaVariantFn = ((props?: any) => string) | ((...args: any[]) => string);

export function createMotionComponent<V extends CvaVariantFn>(
  cvaVariants: V,
  config: MotionCVAConfig = {},
) {
  const finalConfig = { ...DEFAULT_MOTION_CONFIG, ...config };
  const initialVariants = config.variants || getInitialVariants(finalConfig.initial);

  type VariantPropsType = VariantProps<typeof cvaVariants>;
  type ComponentProps = React.HTMLAttributes<HTMLDivElement> &
    VariantPropsType & {
      motionProps?: Partial<HTMLMotionProps<'div'>>;
      animateOnMount?: boolean;
    };

  const MotionCVAComponent = React.forwardRef<HTMLDivElement, ComponentProps>((props, ref) => {
    const {
      className,
      variant,
      size,
      motionProps,
      animateOnMount = finalConfig.animateOnMount,
      ...restProps
    } = props as ComponentProps;
    const reducedMotion = useReducedMotion();
    const disableMotion = Boolean(reducedMotion);

    const cvaClasses = cvaVariants({ variant, size, className });
    const hoverAnimation = getHoverAnimation(finalConfig.hover);
    const tapAnimation = getTapAnimation(finalConfig.tap);
    const focusAnimation = getFocusAnimation(finalConfig.focus);

    const shouldMountAnimate = animateOnMount && !disableMotion;

    const defaultTransition: HTMLMotionProps<'div'>['transition'] =
      (finalConfig.transition as HTMLMotionProps<'div'>['transition']) ?? {
        duration: ANIMATION_DURATIONS.normal,
        ease: EASING.easeOut,
      };

    return (
      <motion.div
        ref={ref}
        className={cvaClasses}
        variants={initialVariants}
        initial={shouldMountAnimate ? 'hidden' : false}
        animate={shouldMountAnimate ? 'visible' : undefined}
        whileHover={!disableMotion && finalConfig.hover !== 'none' ? hoverAnimation : undefined}
        whileTap={!disableMotion && finalConfig.tap !== 'none' ? tapAnimation : undefined}
        whileFocus={!disableMotion && finalConfig.focus !== 'none' ? focusAnimation : undefined}
        transition={defaultTransition}
        {...restProps}
        {...motionProps}
      />
    );
  });

  MotionCVAComponent.displayName = `Motion${cvaVariants.name || 'Component'}`;

  return MotionCVAComponent;
}

/**
 * Hook to get motion props for CVA components with reduced motion support.
 *
 * Automatically respects prefers-reduced-motion media query.
 *
 * @param config - Motion configuration
 * @returns Motion props that respect user preferences
 *
 * @example
 * ```tsx
 * const motionProps = useMotionProps({ hover: 'scale', tap: 'scale' });
 * <motion.button {...motionProps}>Click</motion.button>
 * ```
 */
export function useMotionProps(config: MotionCVAConfig = {}) {
  const finalConfig = { ...DEFAULT_MOTION_CONFIG, ...config };
  const reducedMotion = useReducedMotion();
  const disableMotion = Boolean(reducedMotion);

  return {
    whileHover:
      !disableMotion && finalConfig.hover !== 'none'
        ? getHoverAnimation(finalConfig.hover)
        : undefined,
    whileTap:
      !disableMotion && finalConfig.tap !== 'none' ? getTapAnimation(finalConfig.tap) : undefined,
    whileFocus:
      !disableMotion && finalConfig.focus !== 'none'
        ? getFocusAnimation(finalConfig.focus)
        : undefined,
    transition: finalConfig.transition || {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
    },
  };
}

/**
 * Preset motion configurations for common component types.
 */
export const MOTION_PRESETS = {
  button: {
    hover: 'scale' as const,
    tap: 'scale' as const,
    focus: 'glow' as const,
    initial: 'subtle' as const,
  },
  card: {
    hover: 'lift' as const,
    tap: 'scale' as const,
    focus: 'none' as const,
    initial: 'subtle' as const,
  },
  /** Marketing grids: no mount animation; subtle lift on hover (respects reduced motion) */
  marketingCard: {
    hover: 'lift' as const,
    tap: 'scale' as const,
    focus: 'none' as const,
    initial: 'none' as const,
    animateOnMount: false,
  },
  input: {
    hover: 'none' as const,
    tap: 'none' as const,
    focus: 'glow' as const,
    initial: 'none' as const,
  },
  link: {
    hover: 'scale' as const,
    tap: 'scale' as const,
    focus: 'glow' as const,
    initial: 'none' as const,
  },
} as const;
